import {Image, StyleSheet, type Styles, Text, View} from '@react-pdf/renderer'
import {LABEL_SEPARATOR} from './format'
import {HEADING_SCALE, type LabelFitStep, SMALL_TEXT_PX} from './label-fit'
import {type LabelGeometry, LABEL_SIZES, PX} from './label-sizes'
import {keepWordWhole, type LabelBodyProps} from './preparation-label-pdf'
import type {LabelSubstanceHeadingSize} from './types'

/** a font size with its line height (font + 4) in points */
function text(fontPx: number, lineHeightPx = fontPx + 4) {
  return {fontSize: fontPx * PX, lineHeight: lineHeightPx / fontPx}
}

// styles depend on the fit step, so they are built per label; the sizes are the Figma section 30 notes (CY-421)
function createStyles(geometry: LabelGeometry, step: LabelFitStep, headingSize: LabelSubstanceHeadingSize) {
  const {band, body, page} = geometry
  const headingPx = step.heading * HEADING_SCALE[headingSize]
  return StyleSheet.create({
    labelBox: {
      width: page.width * PX,
      height: page.height * PX,
      flexDirection: 'row',
      fontFamily: 'Label',
      color: '#000',
      backgroundColor: '#fff',
    },
    band: {width: band.width * PX, position: 'relative'},
    bandLine: {
      position: 'absolute',
      right: 0,
      top: band.lineInset * PX,
      bottom: band.lineInset * PX,
      width: band.line * PX,
      backgroundColor: '#000',
    },
    bandImage: {position: 'absolute', top: 0, bottom: 0, left: 0, right: band.line * PX, objectFit: 'fill'},
    body: {
      flex: 1,
      paddingHorizontal: body.paddingX * PX,
      paddingTop: body.paddingTop * PX,
      paddingBottom: body.paddingBottom * PX,
      gap: step.gap * PX,
    },
    patientRow: {flexDirection: 'row', justifyContent: 'space-between'},
    patientName: {...text(step.patientName), fontWeight: 'bold'},
    line: {...text(step.body), letterSpacing: 0.3},
    heading: {...text(headingPx), fontWeight: 'bold', letterSpacing: 0.35},
    administration: {...text(step.body + 3), letterSpacing: 0.3},
    bold: {fontWeight: 'bold'},
    singleLine: {maxLines: 1, textOverflow: 'ellipsis'},
    divider: {height: 2 * PX, backgroundColor: '#000'},
    routeWarning: {
      ...text(SMALL_TEXT_PX, 19),
      backgroundColor: '#000',
      color: '#fff',
      fontWeight: 'bold',
      paddingVertical: 3 * PX,
      paddingHorizontal: 8 * PX,
      maxLines: 2,
    },
    chips: {flexDirection: 'row', flexWrap: 'wrap', gap: 6 * PX},
    chip: {
      ...text(SMALL_TEXT_PX, 20),
      borderWidth: 2 * PX,
      borderColor: '#000',
      paddingVertical: 3 * PX,
      paddingHorizontal: 7 * PX,
      fontWeight: 'bold',
    },
    columns: {flexDirection: 'row', gap: 16 * PX},
    column: {flex: 1, minWidth: 0},
    caption: text(SMALL_TEXT_PX, 18),
    useBy: {...text(step.body + 5), fontWeight: 'bold', letterSpacing: 0.35},
    productionNo: {...text(step.body + 1), letterSpacing: 0.3},
    small: {...text(SMALL_TEXT_PX), letterSpacing: 0.25},
    footer: {marginTop: 'auto'},
    linearBarcode: {width: '100%', height: 40 * PX, marginBottom: 8 * PX, objectFit: 'fill'},
    pharmacy: {
      ...text(SMALL_TEXT_PX),
      borderTopWidth: 1 * PX,
      borderTopColor: '#000',
      paddingTop: 4 * PX,
      letterSpacing: 0.25,
    },
  })
}

/** the 70 × 60 mm label (Figma section 30): the same fields as 100 × 90, compact and stepped down to fit (CY-421) */
export function CompactLabelBody({label, dataMatrixSrc, linearBarcodeSrc, bandTextSrc, captions}: LabelBodyProps) {
  const {fit} = label
  if (!fit) throw new Error('A compact label needs its fit (buildPreparationLabelData)')
  const geometry = LABEL_SIZES[label.size]
  const styles = createStyles(geometry, fit.step, label.substanceHeadingSize)
  const {lines} = fit

  const lineTexts = (texts: string[], style: Styles[string] = styles.line) =>
    texts.map(value => (
      <Text key={value} style={[style, styles.singleLine]}>
        {value}
      </Text>
    ))

  // the name is already shortened to fit (substance-headings.ts); no line limit, so a wrong estimate adds a line
  // instead of cutting the dose (CY-299)
  const headings = label.substanceHeadings.map(heading => (
    <Text key={`${heading.name} ${heading.dose}`} style={styles.heading} hyphenationCallback={keepWordWhole}>
      {heading.name} {heading.dose}
    </Text>
  ))
  const productLines = lineTexts(lines.products)

  const separatorAt = label.pharmacyLine?.indexOf(LABEL_SEPARATOR) ?? -1
  const pharmacyName =
    label.pharmacyLine && separatorAt >= 0 ? label.pharmacyLine.slice(0, separatorAt) : label.pharmacyLine
  const pharmacyRest = label.pharmacyLine && separatorAt >= 0 ? label.pharmacyLine.slice(separatorAt) : ''

  return (
    <View style={styles.labelBox}>
      <View style={styles.band}>
        {bandTextSrc && <Image src={bandTextSrc} style={styles.bandImage} />}
        <View style={styles.bandLine} />
      </View>

      <View style={styles.body}>
        <View style={styles.patientRow}>
          <View style={{width: geometry.patientColumn * PX}}>
            {lineTexts(lines.patientName, styles.patientName)}
            {lineTexts(lines.patientDetails)}
            {lineTexts(lines.ward)}
          </View>
          <Image src={dataMatrixSrc} style={{width: geometry.dataMatrix * PX, height: geometry.dataMatrix * PX}} />
        </View>

        <View style={styles.divider} />

        <View>
          {/* "Trade name before substance" swaps the two blocks (CY-282) */}
          {label.productLinesFirst ? (
            <>
              {productLines}
              {headings}
            </>
          ) : (
            <>
              {headings}
              {productLines}
            </>
          )}
          {lineTexts(lines.carrier)}
          {lineTexts(lines.volume)}
          {lineTexts(label.actualLines)}
          <Text style={styles.administration}>
            {captions.administration}: <Text style={styles.bold}>{label.administrationDate}</Text>
          </Text>
        </View>

        <View style={styles.divider} />

        {label.routeWarning && <Text style={styles.routeWarning}>{captions.routeWarning}</Text>}
        {lines.chips.length > 0 && (
          <View style={styles.chips}>
            {lines.chips.map(chip => (
              <Text key={chip} style={styles.chip}>
                {chip}
              </Text>
            ))}
          </View>
        )}

        <View style={styles.columns}>
          <View style={styles.column}>
            <Text style={styles.caption}>{captions.useBy}</Text>
            <Text style={[styles.useBy, styles.singleLine]}>{label.useBy}</Text>
          </View>
          <View style={styles.column}>
            <Text style={styles.caption}>{captions.productionNo}</Text>
            <Text style={[styles.productionNo, styles.singleLine]}>
              {label.productionNumber.head}
              <Text style={styles.bold}>{label.productionNumber.tail}</Text>
            </Text>
          </View>
        </View>

        {lines.produced.length > 0 && <View>{lineTexts(lines.produced, styles.small)}</View>}
        {label.batchesLine && <Text style={[styles.small, styles.singleLine]}>{label.batchesLine}</Text>}

        {(linearBarcodeSrc || label.pharmacyLine) && (
          <View style={styles.footer}>
            {linearBarcodeSrc && <Image src={linearBarcodeSrc} style={styles.linearBarcode} />}
            {label.pharmacyLine && (
              <Text style={[styles.pharmacy, styles.singleLine]}>
                <Text style={styles.bold}>{pharmacyName}</Text>
                {pharmacyRest}
              </Text>
            )}
          </View>
        )}
      </View>
    </View>
  )
}
